import dayjs, { Dayjs } from 'dayjs'
import {
  CourseDemoPeriodInfo,
  getCourseDemoPeriodInfo,
} from './courseDemoPeriod'

export const COURSE_INDICATORS_SETTINGS_PATH =
  '/v2/platform-settings?tab=addSettingsStudentsPersonAccount#course-indicators-settings'

export interface CourseIndicatorsSettings {
  showNewCourseBadge: boolean
  newCourseBadgeDays: number
}

export interface NewCourseInfo {
  isNew: boolean
  startDate: string | null
  endDate: string | null
  remainingDays: number | null
}

export interface StudentCourseIndicatorsInfo {
  hasDemo: boolean
  hasNewCourse: boolean
  demoOrder: Record<string, any> | null
  newOrder: Record<string, any> | null
}

const defaultSettings: CourseIndicatorsSettings = {
  showNewCourseBadge: true,
  newCourseBadgeDays: 7,
}

const DEMO_EXCLUDED_TARIFF_TYPES = [
  'free',
  'hour',
  'minute',
  'group_per_lesson',
]

export const isDemoAvailableForTariffType = (tariffType?: string | null) =>
  Boolean(tariffType) && !DEMO_EXCLUDED_TARIFF_TYPES.includes(tariffType ?? '')

const parseDate = (value: unknown): Dayjs | null => {
  if (value === null || value === undefined || value === '') return null

  const numericValue = Number(value)
  const parsed =
    Number.isFinite(numericValue) && String(value).trim() !== ''
      ? dayjs(numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue)
      : dayjs(value as string)

  return parsed.isValid() ? parsed : null
}

const normalizeSettingBoolean = (value: unknown, fallback: boolean) => {
  if (value === undefined || value === null || value === '') return fallback
  return value !== false && value !== 0 && value !== '0' && value !== 'false'
}

export const getCourseIndicatorsSettings = (
  settings?: Record<string, unknown> | null,
): CourseIndicatorsSettings => {
  try {
    const platformSettings = settings ?? JSON.parse(
      typeof window === 'undefined'
        ? '{}'
        : window.localStorage.getItem('general') ?? '{}',
    )
    const days = Number(platformSettings?.newCourseBadgeDays)

    return {
      showNewCourseBadge: normalizeSettingBoolean(
        platformSettings?.showNewCourseBadge,
        defaultSettings.showNewCourseBadge,
      ),
      newCourseBadgeDays:
        Number.isFinite(days) && days >= 1 && days <= 365
          ? Math.round(days)
          : defaultSettings.newCourseBadgeDays,
    }
  } catch (error) {
    return defaultSettings
  }
}

export const getResolvedCourseDemoPeriodInfo = (
  order: Record<string, any> | null | undefined,
  currentDate: Dayjs = dayjs(),
): CourseDemoPeriodInfo => {
  return getCourseDemoPeriodInfo(order, currentDate)
}

export const getNewCourseInfo = (
  order: Record<string, any> | null | undefined,
  currentDate: Dayjs = dayjs(),
  settings: CourseIndicatorsSettings = getCourseIndicatorsSettings(),
): NewCourseInfo => {
  if (!order || !settings.showNewCourseBadge) {
    return {
      isNew: false,
      startDate: null,
      endDate: null,
      remainingDays: null,
    }
  }

  const createdAt = parseDate(order?.created_at)
  const initialAccessFrom = parseDate(
    order?.initial_access_from ??
      order?.enroll_date,
  )
  const convertedFromDemoAt = parseDate(
    order?.demo_converted_at ??
      order?.converted_from_demo_at,
  )
  const startCandidates = [createdAt, initialAccessFrom]
    .filter((value): value is Dayjs => Boolean(value))
  const sortedStartCandidates = startCandidates.sort((first, second) =>
    first.valueOf() - second.valueOf(),
  )
  const start = convertedFromDemoAt ??
    sortedStartCandidates[sortedStartCandidates.length - 1]

  if (!start || currentDate.isBefore(start.startOf('day'))) {
    return {
      isNew: false,
      startDate: start?.format('DD.MM.YYYY') ?? null,
      endDate: null,
      remainingDays: null,
    }
  }

  const end = start.add(settings.newCourseBadgeDays - 1, 'day').endOf('day')
  const isNew = !end.isBefore(currentDate)

  return {
    isNew,
    startDate: start.format('DD.MM.YYYY'),
    endDate: end.format('DD.MM.YYYY'),
    remainingDays: isNew
      ? Math.max(Math.ceil(end.diff(currentDate) / 86_400_000), 0)
      : null,
  }
}

const getStudentOrders = (student: Record<string, any>) => {
  const source = [
    ...(Array.isArray(student?.orders) ? student.orders : []),
    ...(Array.isArray(student?.directionOrders) ? student.directionOrders : []),
    ...(Array.isArray(student?.childrenDirection)
      ? student.childrenDirection
      : []),
  ]
  const seen = new Set<string>()

  return source.filter((order) => {
    const key = String(
      order?.id ??
        `${order?.direction_id ?? order?.direction?.id}-${order?.class_id ?? ''}`,
    )
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const getStudentCourseIndicatorsInfo = (
  student: Record<string, any> | null | undefined,
  currentDate: Dayjs = dayjs(),
): StudentCourseIndicatorsInfo => {
  if (!student) {
    return {
      hasDemo: false,
      hasNewCourse: false,
      demoOrder: null,
      newOrder: null,
    }
  }

  const orders = getStudentOrders(student).map((order) => ({
    ...order,
    children_id: order?.children_id ?? student.id,
  }))
  const classOrders = (Array.isArray(student?.classUser)
    ? student.classUser
    : []
  ).map((classUser) => ({
    ...classUser,
    children_id: classUser?.user_id ?? student.id,
    class_id: classUser?.class_id ?? classUser?.class?.id,
    class: classUser?.class,
  }))
  const allOrders = [...orders, ...classOrders]

  const demoOrder =
    allOrders.find(
      (order) => getResolvedCourseDemoPeriodInfo(order, currentDate).isDemo,
    ) ?? null
  const newOrder =
    allOrders.find((order) => getNewCourseInfo(order, currentDate).isNew) ?? null

  return {
    hasDemo: Boolean(demoOrder),
    hasNewCourse: Boolean(newOrder),
    demoOrder,
    newOrder,
  }
}
